import React, { useEffect, useState, useRef, useCallback } from "react";
import DashboardPage from "../../layout";
import { invoiceSummary } from "../../api/reports";
import PaginationComponent from "../../components/Pagination";
import {
  formatCurrentStartDate,
  formatCurrentEndDate,
  getUserInfo,
  getPagePerSize,
  TimeZoneDisplay,
  printElement,
} from "../../utility";
import { getGlobalFilter } from "../../api/filters";
import Export from "../../assets/icons/Export.svg";
import Filter from "../../assets/icons/Filter.svg";
import Loader from "../../components/Loader";
import TimeFrameSelector from "../../components/TimeFrameSelector";
import { formatDate } from "../../utility";
import Close from "../../assets/close-circle.svg";
import Dropdown from "../../components/Dropdown";
import Search from "../../assets/Search.svg";
import Button from "../../components/Button";
import { useTranslation } from "react-i18next";
import ComponentToPrint from "./ComponentToPrint";
import { toast } from "react-toastify";
import moment from "moment";
import DropdownMulti from "../../components/DropdownMultiple";
import debounce from "lodash/debounce";
import { getFinanceReportPositions } from "../../utility/constant";
import { useDateRangeSelector } from "../../components/DateRangeSelector";

const CustomerTable: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const customersPerPage = getPagePerSize();
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [customers, setCustomersData] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState<any>(new Date());
  const [endDate, setEndDate] = useState<any>(new Date());
  const [globalFilters, setGlobalFilters] = useState<any>(null);
  const [region, setRegion] = useState<any>([]);
  const [timeFrame, setSelectTimeFrame] = useState<any>("Today");
  const [sortType, setSortType] = useState<any>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [printableData, setPrintableData] = useState();
  const printRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const [channel, setChannel] = useState<any>([]);
  const [emp, setEmployee] = useState<any>("");
  const [empName, setEmpName] = useState<any>("");
  const [paymentType, setPaymentType] = useState<any>("");
  const [loadingFilter, setFilterLoading] = useState<boolean>(false);
  const [salesOffice, setSalesOffice] = useState<any>([]);
  const [sort, setSort] = useState<any>("");
  const [customerId, setCustomerID] = useState<any>([]);
  const [dateMode, setDateMode] = useState<"picker" | "month">("picker");
  const dateRangeSelector = useDateRangeSelector(2024);
  const [exporting, setExporting] = useState(false);
  const isFinanceSection = localStorage.getItem("section") === "Finance";
  const [filtersApplied, setFiltersApplied] = useState(false);
  const startIndex = (currentPage - 1) * customers?.size;

  useEffect(() => {
    setEmpName(
      globalFilters?.employees?.filter((e: any) => emp === e.id)[0]?.name_eng
    );
  }, [emp]);

  const handleFilterClick = () => {
    setOpen(true);
  };

  function getCurrentDate() {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const getGlobalFilters = async (data: any) => {
    setFilterLoading(true);
    try {
      if (loadingFilter) {
        return;
      }
      const response = await getGlobalFilter(data);
      setGlobalFilters(response);
    } catch (error) {
      console.error("Error fetching global filters:", error);
    } finally {
      setFilterLoading(false);
    }
  };

  const handleClearFilters = () => {
    setEmployee("");
    dateRangeSelector.reset();
    setDateMode("picker");
    setStartDate(getCurrentDate());
    setEndDate(getCurrentDate());
    setOpen(false);
    setSelectTimeFrame("Today");
    setFiltersApplied(false);
    setCustomersData({ items: [], total: 0, size: customersPerPage });
    setCurrentPage(1);
    handleClose();
  };

  const handleFiltersUpdate = () => {
    const data: any = {};

    if (region.length > 0) {
      data["regions"] = region;
    }
    if (salesOffice.length > 0) {
      data["sales_office_ids"] = salesOffice;
    }
    if (emp.length > 0) {
      data["subordinate_ids"] = [emp];
    }

    data["endpoint"] = "invoice-summary";
    data["start_date"] = formatCurrentStartDate(new Date(startDate));
    data["end_date"] = formatCurrentEndDate(new Date(endDate));
    data["positions"] = getFinanceReportPositions(channel);
    getGlobalFilters(data);
  };

  const handleClearApply = (sort: string) => {
    const data: any = {
      start_date: formatCurrentStartDate(new Date()),
      end_date: formatCurrentEndDate(new Date()),
    };
    data["positions"] = getFinanceReportPositions(channel);
    const params = {
      page: 1,
      size: customersPerPage,
    };
    setCurrentPage(1);
    getCustomerData(data, params);
    handleClose();
  };

  const handleChange = (event: any) => {
    updateSearchTerm(event);
    setSearchQuery(event);
  };

  const updateSearchTerm = useCallback(
    debounce((value: string) => {
      handleSearch(value);
    }, 500),
    [
      startDate,
      endDate,
      channel,
      paymentType,
      customerId,
      region,
      salesOffice,
      emp,
      sort,
    ]
  );

  const handleSearch = (value: any) => {
    const data: any = {
      start_date: formatCurrentStartDate(new Date(startDate)),
      end_date: formatCurrentEndDate(new Date(endDate)),
    };
    if (value) {
      data["search"] = value;
    }
    getPayload(data);
    const params = {
      page: 1,
      size: customersPerPage,
    };
    setCurrentPage(1);
    getCustomerData(data, params);
  };

  const handleSortFillter = (data: string) => {
    setSortType(data);
    setIsDropdownOpen(false);
    handleApply(data);
  };

  const handleClose = () => setOpen(false);

  const getCustomerData = async (data: any, params: any) => {
    setCustomersData([]);
    setLoading(true);
    await invoiceSummary(params, data)
      .then((res) => {
        setLoading(false);
        setCustomersData(res.data);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching Customer data:", error);
      });
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateMode("picker");
    setStartDate(e.target.value);
    checkTimeFrame(e.target.value, endDate);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateMode("picker");
    setEndDate(e.target.value);
    checkTimeFrame(startDate, e.target.value);
  };

  const applyYearMonth = (yearVal: number, monthVal: number) => {
    if (!yearVal || !monthVal) return;
    const { start, end } = dateRangeSelector.getMonthRange(yearVal, monthVal);
    const newStartStr = formatDate(start);
    const newEndStr = formatDate(end);
    setStartDate(newStartStr);
    setEndDate(newEndStr);
    setDateMode("month");
    checkTimeFrame(newStartStr, newEndStr);
  };

  const handleMonthYearDropdownChange = (value: string | number) => {
    const yearNum = Number(value);
    dateRangeSelector.setSelectedMonthYear(yearNum);
    if (dateRangeSelector.selectedMonth) {
      const monthNum = Number(dateRangeSelector.selectedMonth);
      if (yearNum === dateRangeSelector.currentYear && monthNum > dateRangeSelector.currentMonth) {
        dateRangeSelector.setSelectedMonth("");
      } else {
        applyYearMonth(yearNum, monthNum);
      }
    }
  };

  const handleMonthDropdownChange = (value: string | number) => {
    const monthNum = Number(value);
    dateRangeSelector.setSelectedMonth(String(monthNum));
    if (dateRangeSelector.selectedMonthYear) {
      applyYearMonth(Number(dateRangeSelector.selectedMonthYear), monthNum);
    }
  };

  useEffect(() => {
    if (!filtersApplied) {
      return;
    }
    const data: any = {
      start_date: formatCurrentStartDate(new Date(startDate)),
      end_date: formatCurrentEndDate(new Date(endDate)),
    };
    getPayload(data);
    const params = {
      page: currentPage,
      size: customersPerPage,
    };
    getCustomerData(data, params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filtersApplied]);

  const checkTimeFrame = (start: any, end: any) => {
    if (start === formatDate(new Date()) && end === formatDate(new Date())) {
      setSelectTimeFrame("Today");
    } else if (
      start === formatDate(new Date().setDate(new Date().getDate() - 1)) &&
      end === formatDate(new Date().setDate(new Date().getDate() - 1))
    ) {
      setSelectTimeFrame("Yesterday");
    } else if (
      start === formatDate(new Date().setDate(1)) &&
      end === formatDate(new Date())
    ) {
      setSelectTimeFrame("Month till date");
    } else {
      setSelectTimeFrame("");
    }
  };

  const getPayload = (data: any) => {
    if (emp) {
      data["emp_id"] = emp;
    }
    if (sort) {
      data["sort"] = [sort];
    }
    data["positions"] = getFinanceReportPositions("");
  };

  const handleApply = (sort: string) => {
    if (!emp || !emp.toString().trim()) {
      return toast.warn("Please Select Employee");
    }
    const data: any = {
      start_date: formatCurrentStartDate(new Date(startDate)),
      end_date: formatCurrentEndDate(new Date(endDate)),
    };
    getPayload(data);
    const params = {
      page: 1,
      size: customersPerPage,
    };
    setCurrentPage(1);
    setFiltersApplied(true);
    getCustomerData(data, params);
    handleClose();
  };

  useEffect(() => {
    const data: any = {};

    if (region.length > 0) {
      data["regions"] = region;
    }
    if (salesOffice.length > 0) {
      data["sales_office_ids"] = salesOffice;
    }
    if (emp) {
      data["subordinate_ids"] = [emp];
    }
    data["endpoint"] = "invoice-summary";
    data["start_date"] = formatCurrentStartDate(new Date(startDate));
    data["end_date"] = formatCurrentEndDate(new Date(endDate));
    data["positions"] = getFinanceReportPositions(channel);
    getGlobalFilters(data);
  }, []);

  const handleExport = async () => {
    if (!emp || !emp.toString().trim()) {
      return toast.warn("Please Select Employee");
    }
    setExporting(true);
    const data: any = {
      start_date: formatCurrentStartDate(new Date(startDate)),
      end_date: formatCurrentEndDate(new Date(endDate)),
      export: true,
      time_zone: TimeZoneDisplay(),
    };

    if (searchQuery) {
      data["search"] = searchQuery;
    }
    if (sortType) {
      data["sort"] = [sortType];
    }
    getPayload(data);

    try {
      const res = await invoiceSummary({}, data);
      toast.success(res?.data?.message);
    } catch (error: any) {
      toast.warn(error?.response?.data?.detail);
    } finally {
      setExporting(false);
    }
  };

  const printData = async () => {
    if (!emp) {
      return toast.warn("Please Select Employee");
    }

    const user_info = getUserInfo();
    const data: any = {
      start_date: formatCurrentStartDate(new Date(startDate)),
      end_date: formatCurrentEndDate(new Date(endDate)),
      emp_id: user_info.emp_id,
    };

    if (emp) {
      data["is_print"] = true;
    }
    getPayload(data);

    const params = {
      page: currentPage,
      size: customersPerPage,
    };

    try {
      const res = await invoiceSummary(params, data);
      setPrintableData(res.data);

      // Wait for React to render the print content
      setTimeout(async () => {
        await printElement("print-content");
      }, 500);
    } catch (error) {
      console.error("Error fetching Customer data:", error);
    }
  };

  useEffect(() => {
    setSalesOffice([]);
    setEmployee("");
  }, [region]);

  useEffect(() => {
    setChannel([]);
    setEmployee("");
  }, [salesOffice]);

  return (
    <DashboardPage>
      <div className="bg-gray-50  sm:pl-0 ">
        {/* Report Title */}
        <div className="bg-white p-4 border-b-2">
          <h1 className="text-l font-bold text-gray-800">
            {t("invoicereport.title")}
          </h1>
        </div>
        <div className="hidden mb-2 sm:flex flex-col sm:flex-row justify-between items-center bg-white p-2 border-b-2 pr-8">
          <div className="w-full sm:w-auto mb-4 sm:mb-0 pl-6">
            <PaginationComponent
              totalCount={customers?.total}
              itemsPerPage={customers?.size}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          </div>

          <div className="flex flex-col sm:flex-row items-center w-full sm:w-auto space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative w-full sm:w-auto">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                <img src={Search} alt={t("masterList.searchPlaceholder")} />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleChange(e.target.value)}
                placeholder={t("DepositInvoice.serachCusid")}
                className="w-full sm:w-auto pl-10 pr-4 py-2 text-sm border rounded text-gray-600"
                style={{ borderColor: "#e0e0e0" }}
              />
            </div>
            <div className="hidden sm:flex space-x-4 items-center relative">
              {isDropdownOpen && (
                <ul className="absolute z-50 right-30 top-10 w-48 bg-white border border-gray-300 rounded shadow-lg">
                  <li
                    className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${sortType === "ASC" && "bg-lightgrey"
                      }`}
                    onClick={() => handleSortFillter("ASC")}
                  >
                    {t("masterList.ascending")}
                  </li>
                  <li
                    className={`px-4 py-2 hover:bg-gray-100 cursor-pointer  ${sortType === "DESC" && "bg-lightgrey"
                      }`}
                    onClick={() => handleSortFillter("DESC")}
                  >
                    {t("masterList.descending")}
                  </li>
                </ul>
              )}
              <button
                onClick={printData}
                className="flex items-center px-4 py-2 text-13 font-medium border rounded transition-all"
              >
                {t("DepositInvoice.print")}
                <img
                  src={Export}
                  alt={t("DepositInvoice.print")}
                  className="w-4 h-4 ml-2"
                />
              </button>
              {isFinanceSection && (
                <button
                  onClick={handleExport}
                  className={`flex items-center gap-2 px-4 py-2 text-13 font-medium border rounded transition-all ${exporting ? "opacity-50 cursor-not-allowed" : ""}`}
                  disabled={exporting}
                >
                  {exporting ? (
                    <span className="flex items-center gap-2">
                      <span className="loader w-4 h-4 border-2 border-t-2 border-gray-300 rounded-full animate-spin"></span>
                      {t("masterList.exporting")}
                    </span>
                  ) : (
                    <>
                      {t("masterList.export")}
                      <img
                        src={Export}
                        alt={t("masterList.export")}
                        className="w-4 h-4"
                      />
                    </>
                  )}
                </button>
              )}
              <button
                onClick={handleFilterClick}
                className="flex items-center gap-2 px-4 py-2 text-13  rounded transition-all"
              >
                <img
                  src={Filter}
                  alt={t("masterList.filter")}
                  className="w-4 h-4 "
                />
                {t("masterList.filter")}
              </button>
              <div></div>

            </div>
          </div>
        </div>
        <div className="sm:hidden flex justify-between items-center p-2 border-b-2 bg-white">
          <div className="relative w-full sm:w-auto">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3">
              {/* Add your search icon here */}
              <img src={Search} alt={t("masterList.searchPlaceholder")} />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={t("masterList.searchPlaceholder")}
              className="w-full sm:w-auto pl-10 pr-4 py-2 text-sm border rounded text-gray-600"
              style={{ borderColor: "#e0e0e0" }}
            />
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={printData}
              className="px-2 py-2 text-gray-600 rounded transition-all"
            >
              <img
                src={Export}
                alt={t("DepositInvoice.print")}
                className="w-4 h-4"
              />
            </button>
            {isFinanceSection && (
              <button
                onClick={handleExport}
                className={`px-2 py-2 text-gray-600 rounded transition-all ${exporting ? "opacity-50 cursor-not-allowed" : ""}`}
                disabled={exporting}
              >
                <img
                  src={Export}
                  alt={t("masterList.export")}
                  className="w-4 h-4"
                />
              </button>
            )}
            <button
              className="px-2 py-2 text-gray-600 rounded transition-all"
              onClick={() => setOpen(!open)}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16m-7 6h7"
                ></path>
              </svg>
            </button>
          </div>
        </div>
        {open && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-end sm:items-start items-center z-50">
            <div className="relative bg-white shadow-lg w-full max-w-md h-full">
              <div className="absolute z-30 top-[50%] left-[45%]">
                {loadingFilter && <Loader />}
              </div>
              <div className="flex justify-between p-6 border-b-2">
                <div className="text-16 font-bold">
                  {t("masterList.filters.title")}
                </div>
                <div>
                  <img
                    className="cursor-pointer"
                    onClick={() => setOpen(false)}
                    src={Close}
                    alt={t("masterList.filters.title")}
                  />
                </div>
              </div>
              <div className="flex flex-col justify-between h-[calc(100vh-100px)] px-12 pt-6 overflow-y-auto scrollbar scrollbar-thin">
                <div className="flex flex-col gap-[30px]">
                  {/* Employee ID Input */}
                  <div className="flex flex-col gap-1">
                    <label className="block text-10 text-gray-700 mb-1">Employee ID</label>
                    <input
                      type="text"
                      value={emp || ""}
                      onChange={(e) => setEmployee(e.target.value)}
                      placeholder="Enter Employee ID"
                      className="rounded p-2 border-2 border-gray-400 w-full text-sm"
                    />
                  </div>
                  <div className="date-range-picker space-y-2">
                    <div className="flex gap-4 justify-between w-full">
                      <div className="w-full">
                        <label className="block text-10 text-gray-700 mb-1">Year</label>
                        <Dropdown
                          label=""
                          options={dateRangeSelector.getMonthYearOptions()}
                          selectedValue={dateRangeSelector.selectedMonthYear}
                          onChange={handleMonthYearDropdownChange}
                          placeholder="Select Year"
                        />
                      </div>
                      <div className="w-full">
                        <label className="block text-10 text-gray-700 mb-1">Month</label>
                        <Dropdown
                          label=""
                          options={dateRangeSelector.getAvailableMonths()}
                          selectedValue={dateRangeSelector.selectedMonth ? Number(dateRangeSelector.selectedMonth) : ""}
                          onChange={handleMonthDropdownChange}
                          placeholder="Select Month"
                        />
                      </div>
                    </div>
                    <div className="flex gap-4 justify-between w-full">
                      <div className="w-full">
                        <label className="block text-10 text-gray-700 mb-1">
                          {t("masterList.filters.startDate")}
                        </label>
                        <input
                          type="date"
                          className={`rounded p-2 border-2 border-gray-400 w-full ${dateMode === "month" ? "bg-gray-100 text-gray-400 cursor-not-allowed opacity-70" : ""}`}
                          value={startDate}
                          max={new Date(endDate).toISOString().split("T")[0]}
                          onChange={handleStartDateChange}
                          onKeyDown={(e) => e.preventDefault()}
                          disabled={dateMode === "month"}
                        />
                      </div>
                      <div className="w-full">
                        <label className="block text-10 text-gray-700 mb-1">
                          {t("masterList.filters.endDate")}
                        </label>
                        <input
                          type="date"
                          className={`rounded p-2 border-2 border-gray-400 w-full ${dateMode === "month" ? "bg-gray-100 text-gray-400 cursor-not-allowed opacity-70" : ""}`}
                          value={endDate}
                          max={new Date().toISOString().split("T")[0]}
                          min={new Date(startDate).toISOString().split("T")[0]}
                          onChange={handleEndDateChange}
                          onKeyDown={(e) => e.preventDefault()}
                          disabled={dateMode === "month"}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="w-full">
                    <TimeFrameSelector
                      customClass={"w-full border border-gray-300 rounded p-2"}
                      setSelectTimeFrame={(tf) => {
                        setDateMode("picker");
                        dateRangeSelector.setSelectedMonthYear("");
                        dateRangeSelector.setSelectedMonth("");
                        setSelectTimeFrame(tf);
                      }}
                      timeFrame={timeFrame}
                      setStartDate={setStartDate}
                      setEndDate={setEndDate}
                    />
                  </div>
                </div>

                <div className="flex justify-between mt-4">
                  <Button
                    onClick={handleClearFilters}
                    label={t("masterList.filters.clearFilters")}
                    type="grey"
                  />
                  <Button
                    onClick={() => handleApply(sortType)}
                    label={t("masterList.filters.applyFilters")}
                    type="purple"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <div
          className="h-[calc(100vh-200px)] md:h-[calc(100vh-120px)] p-2 pt-0 overflow-auto page-container scrollbar scrollbar-thin"
        >
          {!filtersApplied ? (
            <div className="flex items-center justify-center h-full text-gray-500 text-lg font-semibold">
              Apply a filter to see the report.
            </div>
          ) : (
            <table className="min-w-full bg-white border border-black-200 table-auto border-separate border-spacing-0">
              <thead className="bg-gray-200 sticky top-[0px] z-20">
                <tr>
                  <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 whitespace-nowrap">
                    {t("activityreports.tableHeaders.srno")}
                  </th>
                  <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 whitespace-nowrap">
                    {t("DepositInvoice.employeeName")}
                  </th>
                  <th className="px-2 sm:px-4 py-3 border border-gray-300 text-left text-xs sm:text-sm font-bold text-black-500 whitespace-nowrap">
                    {t("DepositInvoice.date")}
                  </th>
                  <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 whitespace-nowrap">
                    {t("invoiceReport.customerId")}
                  </th>
                  <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 whitespace-nowrap">
                    {t("invoiceReport.amount")}
                  </th>
                  <th className="px-2 sm:px-4 py-3 border border-gray-300 text-left text-xs sm:text-sm font-bold text-black-500 whitespace-nowrap">
                    {t("invoiceReport.invoiceId")}
                  </th>
                  <th className="px-2 sm:px-4 py-3 border border-gray-300 text-left text-xs sm:text-sm font-bold text-black-500 whitespace-nowrap">
                    {t("invoiceReport.print")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {customers?.items?.length > 0 &&
                  !loading &&
                  customers.items.map((customer: any, index: number) => (
                    <tr key={customer.emp_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 border text-black-600 whitespace-nowrap">
                        {startIndex + index + 1}
                      </td>
                      <td className="px-4 py-3 border text-black-600 whitespace-nowrap">
                        {customer.employee_name || "-"}
                      </td>
                      <td className="px-4 py-3 border text-black-600 whitespace-nowrap">
                        {moment(customer.date_time).format(
                          "DD-MM-YYYY , hh:mm A"
                        )}
                      </td>
                      <td className="px-4 py-3 border text-black-600 whitespace-nowrap">
                        {customer.customer_id || "-"}
                      </td>
                      <td className="px-4 py-3 border text-black-600 whitespace-nowrap">
                        {customer.amount || "-"}
                      </td>
                      <td className="px-4 py-3 border text-black-600 whitespace-nowrap">
                        {customer.invoice_id || "-"}
                      </td>
                      <td className="px-4 py-3 border text-black-600 whitespace-nowrap">
                        {customer.printed}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
          {filtersApplied && customers?.items?.length === 0 && !loading && (
            <>
              <div className="px-4 py-3 border text-center text-black-600 w-full flex items-center justify-center">
                {t("masterList.noresultsfound")}
              </div>
            </>
          )}
          {filtersApplied && loading && <Loader />}
        </div>
        <div className="sm:hidden bg-white p-2 border-t-2 mt-auto">
          <PaginationComponent
            totalCount={customers?.total}
            itemsPerPage={customers?.size}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </div>
      <div>
        <ComponentToPrint
          data={printableData}
          ref={printRef}
          emp={emp}
          empName={empName}
          date={moment.utc(new Date()).local().format("DD-MM-YYYY hh:mm A")}
          startDate={formatDate(startDate)}
          endDate={formatDate(endDate)}
        />
      </div>
    </DashboardPage>
  );
};

export default CustomerTable;
